import {
  approveProRefundRequest,
  mapClientError,
  type ProAdminRefundDecisionInput,
  type ProRefundRequestDto,
  queryKeys,
  rejectProRefundRequest,
  useAuthStore,
} from "@dutyon/apis";
import { Alert, Button, Card, Select, Stack, Text, Textarea, Title } from "@dutyon/ui-kit";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";
import { prepareApproval, readApproval } from "./approve-model";
import { prepareRejection, readRejection } from "./reject-model";

export function RefundDecisionForm({
  owner,
  id,
  request,
}: {
  owner: string;
  id: string;
  request?: ProRefundRequestDto;
}) {
  const client = useQueryClient();
  const [kind, setKind] = useState<"REJECT" | "APPROVE">("REJECT");
  const approval = kind === "APPROVE";
  const label = approval ? "승인" : "반려";
  const [reason, setReason] = useState(""),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [submitted, setSubmitted] = useState(false);
  const [blockedRevision, setBlockedRevision] = useState<number | null>(null);
  const [pending, setPending] = useState<{
    key: string;
    kind: "REJECT" | "APPROVE";
    maximumAmount: number;
    input: ProAdminRefundDecisionInput;
    uncertain: boolean;
  } | null>(null);
  const lock = useRef(false),
    mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const dirty = !!reason || !!pending;
  const leaveMessage =
    "검토 사유 또는 결과가 확인되지 않은 요청이 있습니다. 이동하면 입력과 재전송 기록을 잃을 수 있습니다. 이동하시겠습니까?";
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search ||
        currentLocation.hash !== nextLocation.hash),
  );
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (window.confirm(leaveMessage)) blocker.proceed();
    else blocker.reset();
  }, [blocker]);

  useEffect(() => {
    if (!dirty) return;
    const unload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const navigate = (event: MouseEvent) => {
      const anchor = (event.target as Element)?.closest?.("a[href]");
      if (!(anchor instanceof HTMLAnchorElement) || event.defaultPrevented) return;
      // Same-origin route navigation is handled by useBlocker, including POP and navigate().
      if (
        anchor.origin === window.location.origin &&
        (!anchor.target || anchor.target === "_self") &&
        !anchor.hasAttribute("download")
      )
        return;
      if (!window.confirm(leaveMessage)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", unload);
    document.addEventListener("click", navigate, true);
    return () => {
      window.removeEventListener("beforeunload", unload);
      document.removeEventListener("click", navigate, true);
    };
  }, [dirty]);
  const active = () => {
    const state = useAuthStore.getState();
    return !!state.token && state.user?.sub === owner && state.user.role === "SUPER_ADMIN";
  };
  const eligible = !!request && ["REQUESTED", "REVIEWING"].includes(request.status);
  async function submit() {
    if (lock.current || submitted || !active()) return;
    let operation = pending;
    if (!operation) {
      if (!eligible || !request || request.revision === blockedRevision) return;
      try {
        operation = {
          key: crypto.randomUUID(),
          kind,
          maximumAmount: request.availableAmount,
          input: approval
            ? prepareApproval(reason, request.revision)
            : prepareRejection(reason, request.revision),
          uncertain: false,
        };
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "사유를 확인해 주세요.");
        return;
      }
    }
    lock.current = true;
    setBusy(true);
    setPending(operation);
    setMessage("");
    let changed = false;
    const unsubscribe = useAuthStore.subscribe(() => {
      if (!active()) changed = true;
    });
    try {
      const result =
        operation.kind === "APPROVE"
          ? readApproval(
              await approveProRefundRequest(owner, id, operation.input, operation.key),
              id,
              operation.input,
              operation.maximumAmount,
            )
          : readRejection(
              await rejectProRefundRequest(owner, id, operation.input, operation.key),
              id,
              operation.input,
            );

      if (!mounted.current || changed || !active()) return;
      setPending(null);
      setReason("");
      setSubmitted(true);
      setMessage(
        result.status === "APPROVED"
          ? `환불 예정액 ${result.amount.toLocaleString("ko-KR")}원으로 승인 기록을 저장했습니다. 실제 환불은 아직 완료되지 않았습니다.`
          : "환불 요청이 반려되었습니다. 결제와 이용권은 변경되지 않습니다.",
      );
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.pro.adminRefundRequest(owner, id) }),
        client.invalidateQueries({
          queryKey: queryKeys.pro.adminRefundRequests(owner).slice(0, 3),
        }),
      ]);
    } catch (error) {
      if (!mounted.current || changed || !active()) return;
      const result = mapClientError(error);
      const unavailable =
        operation.kind === "APPROVE" &&
        result.status === 503 &&
        ["PRO_BILLING_UNAVAILABLE", "PRO_REFUND_UNAVAILABLE"].includes(result.errorCode);
      if (
        !operation.uncertain &&
        ([400, 404, 409, 413].includes(result.status ?? 0) || unavailable)
      ) {
        setPending(null);
        setMessage(
          unavailable
            ? "환불 서비스가 준비되지 않아 승인하지 않았습니다. 검토 사유는 유지됩니다."
            : result.status === 409
              ? "요청이 변경되었습니다. 상세를 다시 조회한 뒤 확인해 주세요."
              : result.message,
        );
        if ([404, 409].includes(result.status ?? 0)) {
          setBlockedRevision(operation.input.expectedRevision);
          void client.invalidateQueries({ queryKey: queryKeys.pro.adminRefundRequest(owner, id) });
        }
      } else {
        setPending({ ...operation, uncertain: true });
        setMessage(
          `${label} 결과를 확인하지 못했습니다. 사유와 요청을 유지한 채 같은 ${label} 요청을 다시 전송해 주세요. 화면을 떠나면 재전송 기록을 잃을 수 있습니다.`,
        );
      }
    } finally {
      unsubscribe();
      lock.current = false;
      if (mounted.current && active()) setBusy(false);
    }
  }
  if (!eligible && !pending && !reason && !message) return null;
  return (
    <Card withBorder>
      <Stack>
        <Title order={3}>환불 요청 {label}</Title>
        <Text size="sm">
          {approval
            ? "원결제의 현재 미환불 잔액 전액을 서버에서 다시 확인해 승인 기록을 저장합니다. 승인만으로 결제나 이용권은 변경되지 않으며, 실제 환불 처리는 별도 단계입니다."
            : "검토 사유를 기록해 이 요청을 종료합니다. 결제와 이용권은 변경하지 않습니다."}
        </Text>
        {message && (
          <Alert color={submitted ? "green" : "red"} role="status">
            {message}
          </Alert>
        )}
        {!submitted && (
          <>
            <Select
              label="처리 방식"
              data={[
                { value: "REJECT", label: "반려" },
                { value: "APPROVE", label: "승인 준비" },
              ]}
              value={kind}
              disabled={busy || !!pending}
              allowDeselect={false}
              onChange={(value) => {
                if (value === "REJECT" || value === "APPROVE") {
                  setKind(value);
                  setMessage("");
                }
              }}
            />
            <Textarea
              label={`${label} 사유`}
              value={reason}
              maxLength={1000}
              rows={3}
              disabled={busy || !!pending}
              onChange={(event) => setReason(event.currentTarget.value)}
            />
            <Button
              color={approval ? "blue" : "red"}
              loading={busy}
              disabled={
                busy ||
                (!pending && (!eligible || !reason.trim() || request?.revision === blockedRevision))
              }
              onClick={() => void submit()}
            >
              {pending ? `같은 ${label} 요청 다시 전송` : `환불 요청 ${label}하기`}
            </Button>
          </>
        )}
      </Stack>
    </Card>
  );
}
